import React from "react";
import { Link } from "react-router-dom";
import { useT } from "@/context/LanguageContext";
import { BRAND, NAV_LINKS, WHATSAPP_LINK, openWhatsApp, SOCIALS } from "@/data/site";
import { COUNTRIES } from "@/data/countries";
import { Envelope, Phone, WhatsappLogo, MapPin, InstagramLogo, FacebookLogo, LinkedinLogo, TiktokLogo, TwitterLogo } from "@phosphor-icons/react";

const SOCIAL_ICONS = { instagram: InstagramLogo, facebook: FacebookLogo, linkedin: LinkedinLogo, tiktok: TiktokLogo, twitter: TwitterLogo };

export function Footer() {
  const { t } = useT();
  return (
    <footer className="bg-ink-950 text-slate-300" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3">
              <div className="overflow-hidden rounded-md bg-[#EEF2FB] p-1">
                <img src={BRAND.logo} alt="Unicorn Valley Group" className="h-10 w-10 object-contain" />
              </div>
              <div>
                <p className="font-heading text-base font-bold text-white">Unicorn Valley</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-teal font-semibold">Global Trade Group</p>
              </div>
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-400">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map((s) => {
                const SIcon = SOCIAL_ICONS[s.key];
                return (
                  <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.key} data-testid={`social-${s.key}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-slate-300 transition-all duration-300 hover:-translate-y-0.5 hover:bg-teal hover:text-white">
                    <SIcon size={18} weight="fill" />
                  </a>
                );
              })}
            </div>
          </div>

          <div>
            <p className="font-heading text-sm font-semibold uppercase tracking-wider text-white">{t("footer.explore")}</p>
            <ul className="mt-4 space-y-3 text-sm">
              {NAV_LINKS.slice(0, 6).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-slate-400 transition-colors hover:text-teal">{t(`nav.${l.key}`)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-heading text-sm font-semibold uppercase tracking-wider text-white">{t("footer.company")}</p>
            <ul className="mt-4 space-y-3 text-sm">
              <li><Link to="/about" className="text-slate-400 hover:text-teal">{t("footer.aboutUs")}</Link></li>
              <li><Link to="/resources" className="text-slate-400 hover:text-teal">{t("footer.resources")}</Link></li>
              <li><Link to="/contact?type=consultation" className="text-slate-400 hover:text-teal">{t("footer.bookConsult")}</Link></li>
              <li><Link to="/contact?type=quote" className="text-slate-400 hover:text-teal">{t("footer.requestQuote")}</Link></li>
              <li><Link to="/login" className="text-slate-400 hover:text-teal">{t("footer.clientLogin")}</Link></li>
            </ul>
          </div>

          <div>
            <p className="font-heading text-sm font-semibold uppercase tracking-wider text-white">{t("footer.getInTouch")}</p>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-3"><Envelope size={18} className="text-teal" /><a href={`mailto:${BRAND.email}`} className="hover:text-teal">{BRAND.email}</a></li>
              <li className="flex items-center gap-3"><Phone size={18} className="text-teal" /><span>{BRAND.phone}</span></li>
              <li className="flex items-center gap-3"><WhatsappLogo size={18} className="text-teal" /><a href={WHATSAPP_LINK} onClick={openWhatsApp} target="_blank" rel="noopener noreferrer" className="hover:text-teal">Chat on WhatsApp</a></li>
              <li className="flex items-start gap-3"><MapPin size={18} className="mt-0.5 text-teal" /><span>Shenzhen · Guangzhou · Ningbo, China</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-8">
          <p className="font-heading text-sm font-semibold uppercase tracking-wider text-white">{t("footer.destinations")}</p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {COUNTRIES.map((c) => (
              <Link key={c.slug} to={`/import/${c.slug}`} className="text-slate-400 transition-colors hover:text-teal" data-testid={`footer-country-${c.slug}`}>
                China → {c.country}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 text-xs text-slate-500 sm:flex-row">
          <p>© {new Date().getFullYear()} Unicorn Valley Group. {t("footer.rights")}</p>
          <p>{t("footer.premium")}</p>
        </div>
      </div>
    </footer>
  );
}
